import { useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useCashRegister } from "../cash-register-context";

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CashEntryDialog({ open, onClose }: Props) {
  const { addEntry } = useCashRegister();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (submitting) return;
    setAmount("");
    setReason("");
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    const t = amount.replace(",", ".").trim();
    const v = parseFloat(t);
    if (!t || Number.isNaN(v) || v <= 0) {
      setError("Informe um valor válido maior que zero.");
      return;
    }
    if (!reason.trim()) {
      setError("Informe o motivo da entrada.");
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await addEntry(v, reason);
      handleClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível registrar a entrada.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 800 }}>Entrada no caixa</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Registre qualquer valor recebido fora de uma venda (ex.: troco de fornecedor, aporte).
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Valor (R$)"
          type="number"
          inputProps={{ min: 0.01, step: 0.01 }}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="Motivo"
          placeholder="Ex.: aporte inicial, troco recebido…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        {error ? (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={submitting} sx={{ textTransform: "none" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={() => void handleSubmit()}
          disabled={submitting}
          sx={{ fontWeight: 800 }}
        >
          Registrar entrada
        </Button>
      </DialogActions>
    </Dialog>
  );
}
